import { useCallback, useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Combobox } from 'medere-ui-framework';
import { searchDeterminations } from '../../../services/StudyResultsController';
import type {
  DeterminationOption,
  DeterminationValue,
} from '../../../services/StudyResultsController';

/** Extends the persisted value with display metadata from the search result. */
export interface DeterminationRow extends DeterminationValue {
  description: string;
  numeric: boolean;
  unit: string | null;
}

export interface DeterminationsStepProps {
  determinations: DeterminationRow[];
  onChange: (determinations: DeterminationRow[]) => void;
}

export default function DeterminationsStep({
  determinations,
  onChange,
}: DeterminationsStepProps) {
  const [selectedDetermination, setSelectedDetermination] = useState<DeterminationOption | null>(null);
  const [valueInput, setValueInput] = useState<string>('');
  const [numericValueInput, setNumericValueInput] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDeterminationSearch = useCallback(
    (props: { filterValue?: string }) =>
      searchDeterminations(props.filterValue ?? '').then((items) => items ?? []),
    [],
  );

  const handleAdd = useCallback(() => {
    setError(null);

    if (!selectedDetermination) {
      setError('Seleccione una determinación.');
      return;
    }

    const duplicate = determinations.some(
      (d) => d.determinationId === selectedDetermination.id,
    );
    if (duplicate) {
      setError('Esta determinación ya fue agregada.');
      return;
    }

    const resolvedValue = selectedDetermination.numeric
      ? numericValueInput != null
        ? String(numericValueInput)
        : ''
      : valueInput;

    if (!resolvedValue.trim()) {
      setError('Ingrese un valor para la determinación.');
      return;
    }

    const newRow: DeterminationRow = {
      determinationId: selectedDetermination.id,
      value: resolvedValue,
      description: selectedDetermination.description,
      numeric: selectedDetermination.numeric,
      unit: selectedDetermination.unit,
    };

    onChange([...determinations, newRow]);

    // Reset inputs
    setSelectedDetermination(null);
    setValueInput('');
    setNumericValueInput(null);
    setError(null);
  }, [selectedDetermination, valueInput, numericValueInput, determinations, onChange]);

  const handleDelete = useCallback(
    (determinationId: number) => {
      onChange(determinations.filter((d) => d.determinationId !== determinationId));
    },
    [determinations, onChange],
  );

  const deleteBodyTemplate = (row: DeterminationRow) => (
    <Button
      icon="pi pi-trash"
      className="p-button-text p-button-rounded p-button-sm determinations-step__delete-btn"
      onClick={() => handleDelete(row.determinationId)}
      aria-label="Eliminar determinación"
    />
  );

  const numericBodyTemplate = (row: DeterminationRow) => (
    <i
      className={`pi ${row.numeric ? 'pi-check' : 'pi-times'} determinations-step__numeric-icon ${
        row.numeric ? 'determinations-step__numeric-icon--yes' : 'determinations-step__numeric-icon--no'
      }`}
    />
  );

  return (
    <div className="determinations-step">
      {/* Add row */}
      <div className="determinations-step__add-row">
        <div className="determinations-step__field">
          <label className="determinations-step__label">Determinación</label>
          <Combobox
            value={selectedDetermination}
            setValue={(val: DeterminationOption | null) => {
              setSelectedDetermination(val);
              setValueInput('');
              setNumericValueInput(null);
              setError(null);
            }}
            getItems={handleDeterminationSearch}
            optionLabel="description"
            placeholder="Buscar determinación..."
            className="border2"
            style={{ width: '100%' }}
          />
        </div>

        <div className="determinations-step__field determinations-step__field--value">
          <label className="determinations-step__label">Valor</label>
          {selectedDetermination?.numeric ? (
            <InputNumber
              value={numericValueInput}
              onValueChange={(e) => setNumericValueInput(e.value ?? null)}
              placeholder="0.00"
              className="determinations-step__input"
              minFractionDigits={0}
              maxFractionDigits={4}
              locale="es-AR"
            />
          ) : (
            <InputText
              value={valueInput}
              onChange={(e) => setValueInput(e.target.value)}
              placeholder="Valor"
              className="determinations-step__input"
            />
          )}
        </div>

        {selectedDetermination?.unit ? (
          <span className="determinations-step__unit-hint">
            {selectedDetermination.unit}
          </span>
        ) : null}

        <Button
          label="Agregar"
          icon="pi pi-plus"
          className="determinations-step__add-btn"
          onClick={handleAdd}
          disabled={!selectedDetermination}
        />
      </div>

      {error ? <div className="determinations-step__error">{error}</div> : null}

      {/* Table */}
      <DataTable
        value={determinations}
        emptyMessage="No se agregaron determinaciones."
        className="determinations-step__table"
        dataKey="determinationId"
        size="small"
        scrollable
      >
        <Column
          body={deleteBodyTemplate}
          style={{ width: '48px', textAlign: 'center' }}
          frozen
        />
        <Column field="description" header="Descripción" />
        <Column
          header="Numérico"
          body={numericBodyTemplate}
          style={{ width: '90px', textAlign: 'center' }}
        />
        <Column field="unit" header="Unidad" style={{ width: '100px' }} />
        <Column field="value" header="Valor" style={{ width: '120px' }} />
      </DataTable>
    </div>
  );
}
